// @flow
import { isEmpty } from 'lodash';
import { getJson } from 'Common/utilities/fetchUtils';

import type { ThunkAction } from 'Common/types';
import type {
  SearchAutoCompletions,
  AiringsResponseShape,
  MoviesAndShowsDolResponse,
  RecentSearchItem,
} from 'Common/search/types';

import type { Recording } from 'Common/dvr/types';
import deviceType from 'Common/app/getDeviceType';

const PLAYER_TYPES_URL_ENCODED = 'widevine%2Cnagra';
const CACHE_EXPIRATION_TIMEOUT = 120; // in seconds
const NATIVE_PAGE_SIZE = deviceType === 'Tv' ? 32 : 30;
export const PAGE_SIZE = process.env.IS_BROWSER ? 35 : NATIVE_PAGE_SIZE;

export const fetchAutoCompletions = (
  keyword: string,
  itemStart?: number = 0,
  totalItems?: number = 15,
): ThunkAction => ({ getState, dispatch, config }) => {
  const state = getState();

  /* NOTE: each new request autocomplete must have new token */
  const token = state.search.autoComplete.completionToken + 1;
  const headers = { Accept: 'application/vnd.echostar.dvr+json;version=3' };

  dispatch({ type: 'REQUEST_AUTOCOMPLETE', token, keyword });

  const url = `${config.session.radish_url}/v20/dol/search/autocomplete.json`;
  const data = {
    term: encodeURIComponent(keyword),
    itemStart,
    totalItems,
  };

  getJson({ url, headers, state, data, caching: { timeout: CACHE_EXPIRATION_TIMEOUT } })
    .then(({ completions }: SearchAutoCompletions) => {
      dispatch({ type: 'RECEIVE_AUTOCOMPLETIONS', payload: completions, token });
    })
    .catch((error: Error) => {
      console.warn('REQUEST_AUTOCOMPLETE fetch error', error);
      dispatch({ type: 'REQUEST_AUTOCOMPLETE_ERROR' });
    });
};

export const resetAutoCompleteResults = () => ({ type: 'RESET_AUTOCOMPLETE' });
export const resetSearchData = () => ({ type: 'RESET_SEARCH_DATA' });

export const fetchShowsAndMovies = (keyword: string, page?: number = 0): ThunkAction => ({
  getState,
  dispatch,
  config,
}) => {
  const state = getState();

  /* Check if cache is valid (compare search keywords) */
  if (state.search.moviesAndShows.searchKeyWord === keyword) {
    const pageFromStore = state.search.moviesAndShows.pages[page];
    if (pageFromStore) {
      /* Request for this page was already made */

      if (pageFromStore.error === false && pageFromStore.fetching === true) {
        /* Request for this page is in progress */
        return undefined;
      }
      if (pageFromStore.fetching === false && pageFromStore.error === false) {
        /* Request for this page was already fulfilled */
        return undefined;
      }
    }
  } else if (state.search.moviesAndShows.searchKeyWord !== '') {
    /* Invalidate cache if new search keyword is set and doesn't equal with previously loaded */
    dispatch({ type: 'INVALIDATE_MOVIES_AND_SHOWS_SEARCH_CACHE' });
  }

  /* Fetch page if it wasn't loaded previously or previous fetch has failed */
  const itemStart = page * PAGE_SIZE + 1;
  const headers = { Accept: 'application/vnd.echostar.dvr+json;version=3' };

  dispatch({ type: 'REQUEST_MOVIES_AND_SHOWS', pageId: page, keyword });

  const url = `${config.session.radish_url}/v20/dol/search/movies_and_shows.json`;
  const data = {
    query: encodeURIComponent(keyword),
    itemStart,
    totalItems: PAGE_SIZE,
    player_types: PLAYER_TYPES_URL_ENCODED,
  };

  return getJson({ url, headers, state, data, caching: { timeout: CACHE_EXPIRATION_TIMEOUT } })
    .then((payload: MoviesAndShowsDolResponse) => {
      dispatch({
        type: 'RECEIVE_MOVIES_AND_SHOWS',
        payload: {
          totalCount: payload.count,
          content: payload.results.map(
            ({
              description,
              content_identification: contentIdentification,
              slug,
              is_locked: isLocked,
              name,
              images: { poster_url: thumbnailUrl, wide_poster_url: widePosterUrl },
              dyna_streams: dynaStreams,
            }) => ({
              description,
              slug,
              is_locked:
                typeof isLocked === 'string'
                  ? isLocked === 'true'
                  : isLocked || (!isEmpty(dynaStreams) && dynaStreams[0].is_locked),
              franchiseName: name,
              thumbnailUrl,
              widePosterUrl,
              content_identification: contentIdentification,
            }),
          ),
          pageId: page,
          keyword,
        },
      });
    })
    .catch((error: Error) => {
      console.warn('REQUEST_MOVIES_AND_SHOWS fetch error', error);
      dispatch({ type: 'REQUEST_MOVIES_AND_SHOWS_ERROR', pageId: page, keyword });
    });
};

export const fetchAirings = (keyword: string, page?: number = 0): ThunkAction => ({
  getState,
  dispatch,
  config,
}) => {
  const state = getState();

  /* Check if cache is valid (compare search keywords) */
  if (state.search.airings.searchKeyWord === keyword) {
    const pageFromStore = state.search.airings.pages[page];
    if (pageFromStore) {
      /* Request for this page was already made */

      if (pageFromStore.error === false && pageFromStore.fetching === true) {
        /* Request for this page is in progress */
        return;
      }
      if (pageFromStore.fetching === false && pageFromStore.error === false) {
        /* Request for this page was already fulfilled */
        return;
      }
    }
  } else if (state.search.airings.searchKeyWord !== '') {
    /* Invalidate cache if new search keyword is set and doesn't equal with previously loaded */
    dispatch({ type: 'INVALIDATE_AIRINGS_SEARCH_CACHE' });
  }

  /* Fetch page if it wasn't loaded previously or previous fetch has failed */
  const url = `${config.session.radish_url}/v20/dol/search/airings.json`;
  const offset = page * PAGE_SIZE;

  const data = {
    query: encodeURIComponent(keyword),
    offset,
    totalItems: PAGE_SIZE,
  };

  dispatch({ type: 'REQUEST_AIRINGS', pageId: page, keyword });

  getJson({ url, state, data, caching: { timeout: CACHE_EXPIRATION_TIMEOUT } })
    .then((payload: AiringsResponseShape) => {
      /* There is a bug in radish - it sends empty array in place of total count (when search returns 0 results).
         Also it may send null in place of 'results'. */

      dispatch({
        type: 'RECEIVE_AIRINGS',
        payload: {
          totalCount: Array.isArray(payload.count) ? 0 : payload.count,
          content: payload.results || [],
          pageId: page,
          keyword,
        },
      });
    })
    .catch((error: Error) => {
      console.warn('REQUEST_AIRINGS fetch error', error);
      dispatch({ type: 'REQUEST_AIRINGS_ERROR', pageId: page, keyword });
    });
};

export const fetchDvrRecordings = (
  keyword: string,
  sort?: string = 'date',
  filter?: string = '',
): ThunkAction => ({ getState, dispatch, config }) => {
  const state = getState();
  const activeReceiverId = state.app.activeReceiver?.receiverId;
  if (!activeReceiverId) {
    return;
  }
  const headers = { Accept: 'application/vnd.echostar.dvr+json;version=2' };

  dispatch({ type: 'REQUEST_DVR_RECORDINGS', keyword });

  const data = { receiver_id: activeReceiverId, sort, filter, startindex: 1, offset: 0 };
  const url = `${config.session.radish_url}dvr`;

  getJson({ url, headers, state, data, caching: { timeout: CACHE_EXPIRATION_TIMEOUT } })
    .then((payload: Array<Recording>) => {
      dispatch({ type: 'RECEIVE_DVR_RECORDINGS', payload: { content: payload, keyword } });
    })
    .catch((error: Error) => {
      console.warn('REQUEST_DVR_RECORDINGS fetch error', error);
      dispatch({ type: 'REQUEST_DVR_RECORDINGS_ERROR', keyword });
    });
};

export const setSearchKeyWord = (keyword: string) => ({
  type: 'SET_SEARCH_KEYWORD',
  payload: keyword,
});
export const resetSearchKeyWord = () => ({ type: 'RESET_SEARCH_KEYWORD' });

export const loadRecentSearches = (): ThunkAction => async () => {
  // FIXME: waiting for an endpoint
};

export const addToRecentSearches = (rs: RecentSearchItem): ThunkAction => async () => {
  // FIXME: waiting for an endpoint
};

export const removeRecentSearch = (rs: RecentSearchItem): ThunkAction => async () => {
  // FIXME: waiting for an endpoint
};

export const fetchTvSearch = (
  keyword: string,
  page?: number = 0,
  type: string = 'all',
): ThunkAction => ({ getState, dispatch, config }) => {
  const state = getState();

  /* Check if cache is valid (compare search keywords) */
  if (
    state.search.moviesAndShows.searchKeyWord === keyword &&
    state.search.moviesAndShows.searchType === type
  ) {
    const pageFromStore = state.search.moviesAndShows.pages[page];
    if (pageFromStore) {
      /* Request for this page was already made */

      if (pageFromStore.error === false && pageFromStore.fetching === true) {
        /* Request for this page is in progress */
        return undefined;
      }
      if (pageFromStore.fetching === false && pageFromStore.error === false) {
        /* Request for this page was already fulfilled */
        return undefined;
      }
    }
  } else if (state.search.moviesAndShows.searchKeyWord !== '') {
    /* Invalidate cache if new search keyword is set and doesn't equal with previously loaded */
    dispatch({ type: 'INVALIDATE_MOVIES_AND_SHOWS_SEARCH_CACHE' });
  }

  /* Fetch page if it wasn't loaded previously or previous fetch has failed */
  const itemStart = page * PAGE_SIZE;
  const headers = { Accept: 'application/vnd.echostar.franchises+json;version=1' };

  dispatch({ type: 'REQUEST_MOVIES_AND_SHOWS', pageId: page, keyword });

  const url = `${config.session.radish_url}tv_search/${!isEmpty(type) ? type : `all`}`;
  const data = {
    query: encodeURIComponent(keyword),
    offset: itemStart,
    totalItems: PAGE_SIZE,
  };
  return getJson({ url, headers, state, data, caching: { timeout: CACHE_EXPIRATION_TIMEOUT } })
    .then((payload: Object) => {
      dispatch({
        type: 'RECEIVE_MOVIES_AND_SHOWS',
        payload: {
          totalCount: payload.items_available,
          content: payload.items.map(
            ({
              slug,
              name,
              images: { poster_url: thumbnailUrl, wide_poster_url: widePosterUrl },
            }) => ({
              slug,
              franchiseName: name,
              thumbnailUrl,
              widePosterUrl,
            }),
          ),
          pageId: page,
          keyword,
          searchType: type,
        },
      });
    })
    .catch((error: Error) => {
      console.warn('REQUEST_MOVIES_AND_SHOWS fetch error', error);
      dispatch({ type: 'REQUEST_MOVIES_AND_SHOWS_ERROR', pageId: page, keyword });
    });
};
